import { motion as m, AnimatePresence } from "framer-motion";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCursorContext } from "../../context/CursorContext";
import { useLanguage } from "../../context/LanguageContext";

const ContactPage: React.FC = () => {
  const { mouseOverEvent, mouseOutEvent } = useCursorContext();
  const { t } = useLanguage();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target as HTMLFormElement);
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const message = formData.get("message") as string;

    // TODO: Replace with your actual Discord Webhook URL
    const DISCORD_WEBHOOK_URL = "https://discord.com/api/webhooks/1451004986111820047/mOoE4panBqyaZjoruIGyaSqDNW-qVvUT3_jir-oWDMzRPfS-1eAxa6fQlZg8bCu2eq8h";

    const payload = {
      username: "Portfolio Notifier",
      avatar_url: "https://i.postimg.cc/05Y5RNP4/carta-e.png",
      embeds: [
        {
          author: {
            name: "Nuevo Mensaje de Contacto",
            icon_url: "https://i.postimg.cc/05Y5RNP4/carta-e.png"
          },
          title: `De: ${name}`,
          description: `**Email:** ${email}`,
          fields: [
            {
              name: "Mensaje",
              value: message,
              inline: false
            }
          ],
          color: 0x000000, // Black color to match the aesthetic
          timestamp: new Date().toISOString(),
          footer: {
            text: "Portfolio Contact Form"
          }
        }
      ]
    };

    try {
      const response = await fetch(DISCORD_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Reset form
        (e.target as HTMLFormElement).reset();

        // Show success message
        setShowSuccess(true);

        // Hide after 3 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      } else {
        console.error("Failed to send to Discord");
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="theme-bg theme-text w-full h-screen flex justify-start items-start md:items-center pt-28 md:pt-0 px-8 md:px-20">
      {/* Main Content Wrapper */}
      <div className="w-full xl1100:w-[50%] h-auto xl1100:h-full flex justify-start md:justify-center xl1100:justify-end items-end xl1100:items-center">
        {/* Text Section */}
        <div className="w-full sm350:w-3/4 h-auto flex flex-col justify-center items-start gap-4">
          {/* Animated Greeting */}
          <div className="overflow-hidden sm:-ml-1 mb-8 sm500:mb-4 xl1100:-mb-[10%]">
            <m.div
              initial={{ transform: "translateY(100%)" }}
              animate={{ transform: "translateY(0%)" }}
              transition={{
                duration: 0.5,
                delay: 1,
              }}
              className="text-[19vw] sm500:text-[21vw] md:text-[16vw] xl1100:text-[14vw] Avegas-Royale-Regular"
            >
              {t("contact.hello")}
            </m.div>
          </div>

          {/* Contact Information */}
          <div className="relative z-30 theme-bg py-10">
            <m.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.75,
                ease: "easeOut"
              }}
              className="w-full sm:w-[90%] flex flex-col gap-6"
            >
              {/* Introduction Text */}
              <div className="text-[12px] leading-5 sm500:text-[14px] sm500:leading-7">
                {t("contact.intro")}
              </div>

              {/* Refined Aesthetic Contact Form */}
              <div className="w-full max-w-2xl mt-8">
                <form onSubmit={handleSubmit} className="flex flex-col gap-10 w-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="relative group">
                      <input
                        type="text"
                        name="name"
                        required
                        className="peer w-full bg-transparent border-b border-current opacity-30 focus:opacity-100 outline-none py-3 transition-all duration-500 placeholder-transparent"
                        placeholder={t("contact.name")}
                      />
                      <label className="absolute left-0 -top-5 Avegas-Royale-Regular text-lg opacity-60 transition-all duration-500 peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-placeholder-shown:opacity-30 peer-focus:-top-5 peer-focus:text-lg peer-focus:opacity-60 pointer-events-none">
                        {t("contact.name")}
                      </label>
                    </div>

                    <div className="relative group">
                      <input
                        type="email"
                        name="email"
                        required
                        className="peer w-full bg-transparent border-b border-current opacity-30 focus:opacity-100 outline-none py-3 transition-all duration-500 placeholder-transparent"
                        placeholder={t("contact.emailLabel")}
                      />
                      <label className="absolute left-0 -top-5 Avegas-Royale-Regular text-lg opacity-60 transition-all duration-500 peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-placeholder-shown:opacity-30 peer-focus:-top-5 peer-focus:text-lg peer-focus:opacity-60 pointer-events-none">
                        {t("contact.emailLabel")}
                      </label>
                    </div>
                  </div>

                  <div className="relative group">
                    <textarea
                      name="message"
                      required
                      rows={1}
                      className="peer w-full bg-transparent border-b border-current opacity-30 focus:opacity-100 outline-none py-3 transition-all duration-500 placeholder-transparent resize-none overflow-hidden"
                      placeholder={t("contact.message")}
                    ></textarea>
                    <label className="absolute left-0 -top-5 Avegas-Royale-Regular text-lg opacity-60 transition-all duration-500 peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-placeholder-shown:opacity-30 peer-focus:-top-5 peer-focus:text-lg peer-focus:opacity-60 pointer-events-none">
                      {t("contact.message")}
                    </label>
                  </div>

                  <div className="flex flex-col md:flex-row justify-between items-center gap-12 mt-12">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      onMouseOver={mouseOverEvent}
                      onMouseOut={mouseOutEvent}
                      className={`group relative px-10 py-4 border border-current overflow-hidden transition-all duration-500 hover:rounded-3xl ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {/* Base Text Layer */}
                      <span className="relative z-0 Avegas-Royale-Regular text-xl uppercase tracking-[0.2em] whitespace-nowrap transition-all duration-500 group-hover:italic">
                        {isSubmitting ? t("contact.sending") : t("contact.send")}
                      </span>

                      {/* Animated Fill Layer with Masked Text */}
                      {!isSubmitting && (
                        <div
                          className="absolute inset-0 z-10 translate-y-[101%] overflow-hidden transition-transform duration-500 ease-out group-hover:translate-y-0"
                          style={{ backgroundColor: 'var(--color-text)' }}
                        >
                          <div className="flex items-center justify-center h-full w-full">
                            <span
                              className="Avegas-Royale-Regular text-xl uppercase tracking-[0.2em] whitespace-nowrap italic"
                              style={{ color: 'var(--color-bg)' }}
                            >
                              {t("contact.send")}
                            </span>
                          </div>
                        </div>
                      )}
                    </button>

                    <div className="flex gap-6 items-center opacity-40 hover:opacity-100 transition-all duration-500">
                      <span className="text-[10px] uppercase tracking-[0.2em] font-light">{t("contact.internet")}</span>
                      <Link
                        to="https://www.linkedin.com/in/estebanvdev"
                        target="_blank"
                        onMouseOver={mouseOverEvent}
                        onMouseOut={mouseOutEvent}
                        className="Avegas-Royale-Regular text-2xl hover:italic transition-all duration-300 border-b border-transparent hover:border-current"
                      >
                        LinkedIn
                      </Link>
                    </div>
                  </div>
                </form>
              </div>
            </m.div>
          </div>
        </div>
      </div>

      {/* Success Notification Pop-up */}
      <AnimatePresence>
        {showSuccess && (
          <m.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
            className="fixed bottom-10 right-10 z-50 theme-bg-inv theme-text-inv px-8 py-4 rounded-2xl shadow-2xl flex items-center gap-4"
          >
            <div className="w-6 h-6 rounded-full border border-current flex items-center justify-center">
              <span className="text-[10px]">✓</span>
            </div>
            <span className="Avegas-Royale-Regular text-xl tracking-wider">
              {t("contact.success")}
            </span>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactPage;
